import PosthogService from '~/services/posthog/PosthogService';

/**
 * Campaign attribution captured from a UTM-tagged link (Branch deep links
 * append `utm_*` to the landing URL on web). `campaign` is the only required
 * field — a link may carry a campaign without a source/medium and we still want
 * to attribute it.
 */
export interface ICampaignAttribution {
  source?: string;
  campaign: string;
  medium?: string;
  content?: string;
  term?: string;
  referringLink?: string;
}

// PostHog person-property names. These MUST stay identical to the mobile app
// (GOTU-3695) so campaigns roll up across both surfaces in reporting.
const buildProperties = (
  prefix: 'initial' | 'latest',
  attribution: ICampaignAttribution,
): Record<string, string> => {
  const candidates: Record<string, string | undefined> = {
    [`${prefix}_utm_source`]: attribution.source,
    [`${prefix}_utm_medium`]: attribution.medium,
    [`${prefix}_utm_campaign`]: attribution.campaign,
    [`${prefix}_utm_content`]: attribution.content,
    [`${prefix}_utm_term`]: attribution.term,
    [`${prefix}_referrer`]: attribution.referringLink,
  };

  // Drop empty values so `$set_once` never claims a key with an empty/undefined
  // value (which would block a real value arriving on a later link).
  return Object.fromEntries(
    Object.entries(candidates).filter(([, value]) => !!value),
  ) as Record<string, string>;
};

/**
 * Write campaign attribution as PostHog person properties on the current
 * profile — the anonymous person before login (which PostHog merges into the
 * office on identify) or the logged-in office directly.
 *
 * - First-touch (`$set_once`): `initial_*` — sticks forever to the first campaign.
 * - Last-touch (`$set`): `latest_*` — updates to the most recent campaign.
 *
 * Intentionally writes only to the current profile and is NOT replayed on later
 * identifies, so a campaign never leaks onto a different office that logs in on
 * the same browser. The pre-login case is covered by PostHog's
 * anonymous→identify person stitching.
 */
export const setCampaignAttribution = (attribution: ICampaignAttribution): void => {
  PosthogService.getInstance().setPersonProperties(
    buildProperties('latest', attribution),
    buildProperties('initial', attribution),
  );
};
